import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault } from '../lib/vault/vaultApi';
import {
  inviteExecutor,
  listExecutors,
  removeExecutor,
  ORDER_COPY,
  type Executor,
} from '../lib/executors/executorApi';
import './guardians.css';

function formatDate(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ExecutorsScreen() {
  const { api } = useAuth();
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [executors, setExecutors] = useState<Executor[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const dash = await getMyVault(api);
      setVaultId(dash.vault.id);
      const res = await listExecutors(api, dash.vault.id);
      setExecutors(res.executors);
    } catch {
      setError('We could not reach your executors just now.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleResend(e: Executor): Promise<void> {
    if (vaultId === null) return;
    setBusy(true);
    setError(null);
    try {
      await inviteExecutor(api, vaultId, e.id);
      setNotice(`We sent ${e.executor_name} their invitation again.`);
      await load();
    } catch {
      setError('That invitation could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(e: Executor): Promise<void> {
    if (vaultId === null) return;
    setBusy(true);
    setError(null);
    try {
      await removeExecutor(api, vaultId, e.id);
      setNotice(`${e.executor_name} is no longer an executor.`);
      await load();
    } catch {
      setError('We could not remove them.');
    } finally {
      setBusy(false);
    }
  }

  const live = (executors ?? []).filter((e) => e.status === 'invited' || e.status === 'active');
  const hasPrimary = live.some((e) => e.order_index === 1);
  const hasBackup = live.some((e) => e.order_index === 2);

  return (
    <section className="guard">
      <p className="guard__kicker">Your named executor</p>
      <h1 className="guard__title">Executor.</h1>
      <p className="guard__sub">
        One person you trust — no matter what — to release your vault when you are gone. Not a vote,
        not a committee: a single named administrator. You can name a backup who steps in only if
        your primary cannot act. They authorize the release; each message still goes only to the
        person it was written for.
      </p>

      {error !== null && (
        <p role="alert" className="guard__alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="guard__notice">
          {notice}
        </p>
      )}

      {executors !== null && live.length === 0 && (
        <div className="guard__card guard__empty">
          <h2 className="guard__cardTitle">You have not named an executor.</h2>
          <p className="guard__body">
            Naming one gives a single trusted person the authority to open your vault to your people
            when the time comes — without waiting on anyone else. Choose someone who would still be
            standing when it matters.
          </p>
          <div className="la-cta-wrap">
            <Link to="/executors/new?order=1" className="la-btn-primary">
              Name your primary executor
            </Link>
          </div>
        </div>
      )}

      {executors !== null && live.length > 0 && (
        <>
          <ul className="guard__list">
            {live
              .slice()
              .sort((a, b) => a.order_index - b.order_index)
              .map((e) => (
                <li key={e.id} className="guard__card guard__item">
                  <div className="guard__itemHead">
                    <div>
                      <h3 className="guard__name">{e.executor_name}</h3>
                      <p className="guard__meta">{e.executor_email}</p>
                    </div>
                    <span className={`guard__pill guard__pill--${e.status}`}>
                      {e.status === 'active' ? 'Accepted' : 'Invited'}
                    </span>
                  </div>

                  <p className="guard__role">
                    <strong>{ORDER_COPY[e.order_index].label}</strong> —{' '}
                    {ORDER_COPY[e.order_index].blurb}
                  </p>

                  {e.status === 'invited' && (
                    <p className="guard__body guard__waiting">
                      Invited {formatDate(e.invited_at)}. They have not accepted yet, so they cannot
                      act on your behalf until they do.
                    </p>
                  )}

                  {e.status === 'active' && e.order_index === 2 && hasPrimary && (
                    <p className="guard__body guard__waiting">
                      As your backup, {e.executor_name} can act only if your primary executor cannot.
                    </p>
                  )}

                  <div className="guard__actions">
                    {e.status === 'invited' && (
                      <button
                        type="button"
                        className="la-btn-secondary"
                        disabled={busy}
                        onClick={() => void handleResend(e)}
                      >
                        Send again
                      </button>
                    )}
                    <button
                      type="button"
                      className="la-btn-secondary"
                      disabled={busy}
                      onClick={() => void handleRemove(e)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
          </ul>

          {!hasPrimary && (
            <div className="la-cta-wrap guard__addWrap">
              <Link to="/executors/new?order=1" className="la-btn-primary">
                Name your primary executor
              </Link>
            </div>
          )}
          {hasPrimary && !hasBackup && (
            <div className="la-cta-wrap guard__addWrap">
              <Link to="/executors/new?order=2" className="la-btn-primary">
                Name a backup executor
              </Link>
            </div>
          )}
          {hasPrimary && hasBackup && (
            <p className="guard__body guard__full">
              You have named a primary and a backup. That is enough — the backup steps in only if
              your primary cannot act.
            </p>
          )}
        </>
      )}
    </section>
  );
}
